/**
 * S9 · Your account (design files 9 / 9w).
 *
 * Signed-in consumer home: card list with real status, order-scoped personal
 * details, and (when a real waitlist entry exists) the referral block. No
 * money figures, payout progress, PayPal details or activity feed are
 * rendered unless the schema actually carries them — the design's balance,
 * "codes used" and payout tiles are omitted rather than faked.
 *
 * LEGAL GATES (L1): no scheme name/mark, no patent claim, no present-tense
 * issuance claim, no fabricated money figures.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ContextSwitcher from "@/components/ContextSwitcher";
import { getUser } from "@/services/auth";
import { listCardRequests, type CardRequestRow } from "@/services/card-requests";
import { getUserWaitlistEntry, type WaitlistEntryRow } from "@/services/waitlist";
import { FALLBACK_TILE_COLOR } from "@/lib/style-art";
import { toCssColour } from "@/lib/color";
import { listThemeTiles } from "@/services/style-previews";
import { humaniseStyleKey, listStyleDisplayNames } from "@/services/style-names";
import { getGenerationUrls } from "@/services/avatar";
import DesignedCardFace from "@/components/consumer/DesignedCardFace";
import { readDesignedCard } from "@/lib/designed-card";
import { readCardCustomization, readStylePick } from "@/lib/consumer-session";
import { ShareCardPanel } from "@/components/consumer/ShareCardPanel";
import { S5_SECTION, S5_DEFAULTS } from "@/content/screens/s5";
import { LAUNCH_PROGRAM_SLUG } from "@/services/programs";
import { repeatEntryPath, postalAddressSummary } from "@/lib/repeat-flow";
import {
  Display,
  FunnelContainer,
  FunnelPage,
  Pill,
  SectionTitle,
  Wordmark,
} from "@/components/consumer/chrome";
import { useSiteContent } from "@/hooks/useSiteContent";
import ProfileAvatar from "@/components/consumer/ProfileAvatar";
import ProfileDetailsEditor from "@/components/consumer/ProfileDetailsEditor";
import EmailChangeSection from "@/components/consumer/EmailChangeSection";
import MySupportTickets from "@/components/support/MySupportTickets";
import ReferralRecord from "@/components/consumer/ReferralRecord";
import { useReferralTerms, resolveRewardCopy } from "@/lib/referral-terms";
import { getMyReferralSummary } from "@/services/referrals";

import AvatarLibraryDialog from "@/components/consumer/AvatarLibraryDialog";
import { getProfileDisplay, setProfileDisplay, listOwnAvatarsPage, type OwnAvatar } from "@/services/profile-display";

/** How many avatars are shown inline on My Account. The rest live in "Other". */
const RECENT_AVATAR_COUNT = 5;
import { notifyProfileDisplayChanged } from "@/hooks/use-user-avatar";
import { resolveConsumerColours, type ConsumerColour } from "@/lib/consumer-colours";
import { getPublicProgramBySlug } from "@/services/programs";
import { S9_SECTION, S9_DEFAULTS } from "@/content/screens/s9";

function buildStatusCopy(t: (key: string) => string): Record<string, string> {
  return {
    pending: t("s9.cards.status.pending"),
    submitted: t("s9.cards.status.pending"),
    waitlisted: t("s9.cards.status.waitlisted"),
    approved: t("s9.cards.status.approved"),
    fulfilled: t("s9.cards.status.approved"),
    rejected: t("s9.cards.status.rejected"),
  };
}

const STATUS_TONE: Record<string, string> = {
  pending: "bg-amber text-ink",
  submitted: "bg-amber text-ink",
  waitlisted: "bg-amber text-ink",
  approved: "bg-leaf/15 text-leaf",
  fulfilled: "bg-leaf/15 text-leaf",
  rejected: "bg-[#C41112]/10 text-[#C41112]",
};

function addressLine(row: CardRequestRow | null | undefined): string | null {
  if (!row) return null;
  if (row.delivery_type === "branch" || row.delivery_type === "in_branch") return "your branch";
  // ONE resolver: `region`/`postcode` are canonical, `state`/`zip`/`postalCode`
  // are legacy keys. Reading raw keys here is what made State/ZIP vanish.
  return postalAddressSummary(row.postal_address as Record<string, string> | null);
}

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("en-US", { day: "numeric", month: "short" });
  } catch {
    return null;
  }
}

function cardSubtitle(row: CardRequestRow, t: (key: string) => string): string {
  const date = formatDate(row.reviewed_at || row.submitted_at || row.created_at);
  switch (row.status) {
    case "approved":
    case "fulfilled":
      return date ? t("s9.cards.subtitle.approved").replace("{date}", date) : t("s9.cards.subtitle.approvedNoDate");
    case "rejected":
      return date ? t("s9.cards.subtitle.rejected").replace("{date}", date) : t("s9.cards.subtitle.rejectedNoDate");
    case "waitlisted":
      return t("s9.cards.subtitle.waitlisted");
    default:
      return date ? t("s9.cards.subtitle.submitted").replace("{date}", date) : t("s9.cards.subtitle.submittedNoDate");
  }
}

export default function S9Account() {
  const navigate = useNavigate();
  const { t } = useSiteContent(S9_SECTION, S9_DEFAULTS);
  /** Share copy lives with the share panel's own screen keys — one source. */
  const { t: ts } = useSiteContent(S5_SECTION, S5_DEFAULTS);
  const [email, setEmail] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [cards, setCards] = useState<CardRequestRow[]>([]);
  const [waitlist, setWaitlist] = useState<WaitlistEntryRow | null>(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  // Profile display: the user's OWN generated avatars + the SAME palette the
  // cards use (programs.public_config.colorChoices) — one palette, not two.
  //
  // SCOPE: only the 5 most recent are rendered inline. The full history (200+
  // on some accounts) lives behind "Other", paged on demand.
  const [generations, setGenerations] = useState<{ id: string; generated_image_url: string | null }[]>([]);
  const [libraryOpen, setLibraryOpen] = useState(false);
  /** Is there anything beyond the recent five? Drives the "Other" affordance. */
  const [hasMoreAvatars, setHasMoreAvatars] = useState(false);
  /** URL of a pick made in the library, which may not be in the recent five. */
  const [pickedUrl, setPickedUrl] = useState<string | null>(null);
  const [palette, setPalette] = useState<ConsumerColour[]>(() => resolveConsumerColours(null));
  const [selectedGenId, setSelectedGenId] = useState<string | null>(null);
  const [bgColour, setBgColour] = useState<string>("#1C2030");
  const [savedDisplay, setSavedDisplay] = useState<{ id: string | null; colour: string | null }>({ id: null, colour: null });
  const [saving, setSaving] = useState(false);
  const [savedNote, setSavedNote] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  /**
   * N17/N18 · three sections, Share first. This is presentation only — the same
   * data, the same components, nothing new collected.
   */
  const [section, setSection] = useState<"share" | "cards" | "profile">("share");
  /**
   * N19 · a card row stores the CHOSEN generation id, not its image, so the
   * ordered cards rendered as bare colour blocks. Resolve id -> URL here and
   * render through the SAME card renderer the funnel uses.
   */
  const [cardArt, setCardArt] = useState<Record<string, string>>({});
  /**
   * N19b · the ALLOCATED gallery style (display name + template image), keyed by
   * style id. Without it a legacy style renders its raw slug as a title and a
   * blank card, because the local art registry has no entry for it.
   */
  const [styleMeta, setStyleMeta] = useState<Record<string, { name: string; preview: string | null; color: string | null }>>({});
  /* L15 · referral figures come from config, and a referral reward is a COUNT of
     fee discounts — never a money balance. */
  const referralTerms = useReferralTerms();
  const [discountsEarned, setDiscountsEarned] = useState<number | null>(null);
  /* B7 · the ISSUED code (referral_codes, minted on first order). Distinct from
     the waitlist queue code: an ordering customer has one whether or not they
     ever joined a waitlist, and it is the code a share link carries. */
  const [issuedCode, setIssuedCode] = useState<string | null>(null);
  const [referralsMade, setReferralsMade] = useState<number | null>(null);
  useEffect(() => {
    let live = true;
    getMyReferralSummary().then(({ data }) => {
      if (live && data) {
        setDiscountsEarned((data.discounts_unspent ?? 0) + (data.discounts_used ?? 0));
        setIssuedCode(data.code ?? null);
        setReferralsMade(data.referrals_made ?? 0);
      }
    });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: auth } = await getUser();
      if (cancelled) return;
      if (!auth?.user) {
        setLoading(false);
        return;
      }
      setEmail(auth.user.email ?? null);
      setUserId(auth.user.id);

      const { data: rows } = await listCardRequests();
      if (cancelled) return;
      const mine = ((rows as unknown as CardRequestRow[]) || []).filter((r) => r.user_id === auth.user.id);
      setCards(mine);
      const art = await getGenerationUrls(
        mine.map((r) => r.selected_avatar_image_id).filter(Boolean) as string[],
      );
      if (cancelled) return;
      setCardArt(art);

      const tenantId = mine[0]?.tenant_id;
      if (tenantId) {
        const { data: entry } = await getUserWaitlistEntry(tenantId, auth.user.id);
        if (!cancelled) setWaitlist(entry ?? null);
      }
      // Profile display sources, all owner-scoped by RLS. Only the 5 most
      // recent avatars are fetched here — never the whole history.
      const [recent, chosen, programRes, tiles, styleNames] = await Promise.all([
        listOwnAvatarsPage(auth.user.id, 0, RECENT_AVATAR_COUNT),
        getProfileDisplay(auth.user.id),
        getPublicProgramBySlug(LAUNCH_PROGRAM_SLUG),
        listThemeTiles(),
        listStyleDisplayNames(),
      ]);
      if (cancelled) return;
      const gens = recent.rows;
      setGenerations(gens);
      setHasMoreAvatars(recent.hasMore);
      const gallery: Record<string, { name: string; preview: string | null; color: string | null }> = {};
      /* STYLE (look) keys first — an older card row stores `bitmoji_inspired`,
         whose customer-facing name ("Cardmoji") lives on the style row. Named
         here, not special-cased in the card component. */
      for (const [key, name] of Object.entries(styleNames)) {
        gallery[key] = { name, preview: null, color: null };
      }
      /* Then every theme tile (names EVERY slug, including ones this program no
         longer exposes), then the program gallery adds the template image. */
      for (const row of tiles.data) {
        gallery[row.style_id] = { name: row.display_name || humaniseStyleKey(row.style_id), preview: null, color: row.color };
      }
      for (const st of programRes.data?.styles ?? []) {
        gallery[st.style_id] = {
          name: st.display_name || gallery[st.style_id]?.name || humaniseStyleKey(st.style_id),
          preview: st.preview_image_url ?? null,
          color: st.color ?? gallery[st.style_id]?.color ?? null,
        };
      }
      setStyleMeta(gallery);
      const swatches = resolveConsumerColours((programRes.data?.config as any)?.colorChoices);
      setPalette(swatches);
      setSelectedGenId(chosen.generationId ?? gens[0]?.id ?? null);
      // The saved pick can be older than the recent five — keep its URL so the
      // large preview still renders it.
      setPickedUrl(chosen.avatarUrl ?? null);
      setBgColour(chosen.backgroundColour ?? swatches[0]?.colour ?? "#1C2030");
      setSavedDisplay({ id: chosen.generationId, colour: chosen.backgroundColour });

      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const dirty = selectedGenId !== savedDisplay.id || bgColour !== savedDisplay.colour;
  const profileAvatarUrl =
    generations.find((g) => g.id === selectedGenId)?.generated_image_url ?? pickedUrl;

  async function saveProfileDisplay() {
    setSaving(true);
    setSaveError(null);
    setSavedNote(null);
    const { error } = await setProfileDisplay(selectedGenId, bgColour);
    setSaving(false);
    if (error) {
      setSaveError("Couldn't save. Try again.");
      return;
    }
    setSavedDisplay({ id: selectedGenId, colour: bgColour });
    setSavedNote("Saved");
    // ONE source of truth: push the saved pair (avatar AND colour) so the
    // account/switcher button flips in the same commit as this section.
    notifyProfileDisplayChanged({ avatarUrl: profileAvatarUrl ?? null, backgroundColour: bgColour });
    setTimeout(() => setSavedNote(null), 2000);

  }

  async function copyCode() {
    const code = issuedCode ?? waitlist?.referral_code;
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  }

  /**
   * N18 · the share detail carried from the previous screen. Same session
   * objects the funnel's own share screen reads — no second store.
   */
  const sessionCard = readDesignedCard(LAUNCH_PROGRAM_SLUG);
  const sharedCustom = readCardCustomization(LAUNCH_PROGRAM_SLUG);
  const sharedPick = readStylePick(LAUNCH_PROGRAM_SLUG);
  /**
   * The account is reached long after the funnel session has gone, so the share
   * panel falls back to the customer's OWN newest ordered card (same renderer,
   * same resolved avatar as the Cards list) instead of an empty card.
   */
  const newestCard = cards[0];
  const newestAvatar = newestCard?.selected_avatar_image_id
    ? cardArt[newestCard.selected_avatar_image_id] ?? null
    : null;
  const sharedCard =
    sessionCard.avatarUrl || !newestCard
      ? sessionCard
      : {
          avatarUrl: newestAvatar,
          colour: toCssColour(newestCard.card_color) || styleMeta[newestCard.style ?? ""]?.color || FALLBACK_TILE_COLOR,
          orientation: ((newestCard.orientation as "portrait" | "landscape") || "portrait"),
          avatarZoom: Number.isFinite(Number(newestCard.avatar_scale))
            ? Math.min(200, Math.max(50, Number(newestCard.avatar_scale) * 100))
            : 100,
          avatarPosition: {
            x: Number.isFinite(Number((newestCard.avatar_position as any)?.x)) ? Number((newestCard.avatar_position as any).x) : 50,
            y: Number.isFinite(Number((newestCard.avatar_position as any)?.y)) ? Number((newestCard.avatar_position as any).y) : 50,
          },
        };
  const sharedStyleId = sessionCard.avatarUrl ? sharedPick?.style_id : newestCard?.style;
  const sharedStyleName =
    (sharedStyleId ? styleMeta[sharedStyleId]?.name : null) ??
    "your card";

  const primary = cards[0];
  const displayName = primary ? `${primary.first_name} ${primary.last_name}`.trim() : null;
  const posted = addressLine(primary);
  const sharingTerms = resolveRewardCopy(t("s9.sharing.terms"), referralTerms);

  return (
    <FunnelPage testId="s9-account" pb="pb-16">
      <div className="border-b border-foreground/10 bg-white">
        <FunnelContainer size="account" className="flex items-center gap-3 py-3.5 pt-3.5 md:py-4">
          <Wordmark to="/" />
          <span className="flex-1" />
          {email ? <span className="hidden text-[14px] font-medium text-slate-muted sm:inline">{email}</span> : null}
          {/* ONE identity control, same pattern as the landing nav: avatar (or
              person icon) -> contexts, then screen shortcuts, then log out last.
              The old separate "Switch" pill + initials menu were two controls
              doing one job. */}
          <ContextSwitcher
            variant="avatar"
            extraItems={
              <Link
                to="/create/style"
                onClick={() => repeatEntryPath(LAUNCH_PROGRAM_SLUG)}
                data-testid="s9-menu-add-card"
                className="flex min-h-11 items-center rounded-[10px] px-2 py-2 text-[14px] font-medium text-ink hover:bg-muted"
              >
                {t("s9.menu.addAnotherCard")}
              </Link>
            }
          />
        </FunnelContainer>
      </div>

      <FunnelContainer size="account" className="pt-[26px] md:pt-[34px]">
        <Display className="mb-4">{t("s9.header.title")}</Display>

        {/* N17 · three sections. Share opens by default (N18). */}
        <div className="mb-[22px] flex gap-2" data-testid="s9-sections" data-active={section}>
          {([
            ["share", t("s9.tabs.share")],
            ["cards", t("s9.tabs.cards")],
            ["profile", t("s9.tabs.profile")],
          ] as const).map(([id, label]) => (
            <Pill key={id} active={section === id} onClick={() => setSection(id)} data-testid={`s9-section-${id}`}>
              {label}
            </Pill>
          ))}
        </div>

        {loading ? (
          <p className="text-[15px] text-foreground/60" data-testid="s9-loading">
            {t("s9.body.loading")}
          </p>
        ) : !userId ? (
          <p className="text-[15px] text-foreground/60" data-testid="s9-signed-out">
            {t("s9.body.signedOut")}{" "}
            {/* The genuine sign-in affordance: the ONE unified login, returning here. */}
            <Link to="/login?next=%2Faccount" className="text-azure hover:text-tangerine" data-testid="s9-signin-link">
              {t("s9.body.signInLink")}
            </Link>{" "}
            {t("s9.body.signedOutSuffix")}{" "}
            {/* Relabelled: this link starts the funnel — it was previously mislabelled "Sign in". */}
            <Link to="/create" className="text-azure hover:text-tangerine" data-testid="s9-start-creating-link">
              {t("s9.body.startCreating")}
            </Link>
          </p>
        ) : (
          <>
            {/* N18 · Share first, and it carries the share detail from the
                previous screen through the SAME share panel /create/share uses. */}
            {section === "share" ? (
              <div className="mb-[30px]" data-testid="s9-share-section">
                <ShareCardPanel
                  card={sharedCard}
                  styleName={sharedStyleName}
                  styleId={sharedStyleId}
                  
                  t={ts}
                  initialFormat={sharedCustom?.format ?? "story"}
                  initialCaption={sharedCustom?.caption}
                />
              </div>
            ) : null}

            {/* Profile */}
            {section === "profile" ? (
            <div className="mb-3.5 rounded-[20px] border border-foreground/10 bg-white p-5">
              <div className="mb-4 flex items-center gap-3">
                <span className="text-[12px] font-semibold uppercase tracking-[0.1em] text-slate-muted">{t("s9.profile.sectionLabel")}</span>
                <span className="flex-1" />
              </div>

              {/* Profile display: the customer's own cutout on their chosen
                  colour, rendered through the ONE avatar renderer. */}
              <div className="mb-5 flex items-start gap-4">
                <ProfileAvatar
                  avatarUrl={profileAvatarUrl}
                  backgroundColour={bgColour}
                  alt={displayName ?? "Your profile avatar"}
                  className="w-[92px] flex-none"
                  data-testid="s9-profile-avatar"
                />
                <div className="min-w-0 flex-1">
                  <span className="text-[12px] font-semibold uppercase tracking-[0.06em] text-slate-muted">Profile avatar</span>
                  {generations.length === 0 ? (
                    <p className="mt-1 text-[14px] text-foreground/70" data-testid="s9-avatar-picker-empty">
                      You haven't made an avatar yet.{" "}
                      <Link to="/create/style" className="text-azure hover:text-tangerine">Create one</Link>.
                    </p>
                  ) : (
                    <>
                      <div className="mt-2 flex flex-wrap gap-2" data-testid="s9-avatar-picker">
                        {generations.map((g) => (
                          <button
                            key={g.id}
                            type="button"
                            aria-pressed={selectedGenId === g.id}
                            data-testid={`s9-avatar-option-${g.id}`}
                            onClick={() => {
                              setSelectedGenId(g.id);
                              setPickedUrl(g.generated_image_url);
                            }}
                            className={`h-[54px] w-[54px] overflow-hidden rounded-full border-2 transition-colors ${
                              selectedGenId === g.id ? "border-grape" : "border-transparent hover:border-ink/25"
                            }`}
                          >
                            <ProfileAvatar avatarUrl={g.generated_image_url} backgroundColour={bgColour} className="h-full w-full" />
                          </button>
                        ))}

                        {/* "Other" — the rest of the history, paged on demand so
                            200 thumbnails are never mounted here. */}
                        {hasMoreAvatars ? (
                          <button
                            type="button"
                            onClick={() => setLibraryOpen(true)}
                            data-testid="s9-avatar-other"
                            className="flex h-[54px] w-[54px] flex-col items-center justify-center rounded-full border-2 border-dashed border-ink/25 text-[11px] font-semibold text-ink hover:border-ink/50"
                          >
                            Other
                          </button>
                        ) : null}
                      </div>


                      <span className="mt-4 block text-[12px] font-semibold uppercase tracking-[0.06em] text-slate-muted">
                        Background colour
                      </span>
                      <div className="mt-2 flex flex-wrap gap-2" data-testid="s9-bg-picker">
                        {palette.map((c) => (
                          <button
                            key={c.id}
                            type="button"
                            title={c.cname}
                            aria-label={c.cname}
                            aria-pressed={bgColour.toLowerCase() === c.colour.toLowerCase()}
                            data-testid={`s9-bg-option-${c.id}`}
                            onClick={() => setBgColour(c.colour)}
                            className={`h-[30px] w-[30px] rounded-full border-2 ${
                              bgColour.toLowerCase() === c.colour.toLowerCase() ? "border-ink" : "border-transparent hover:border-ink/25"
                            }`}
                            style={{ background: c.colour }}
                          />
                        ))}
                      </div>

                      <div className="mt-4 flex items-center gap-3">
                        <button
                          type="button"
                          disabled={!dirty || saving}
                          onClick={saveProfileDisplay}
                          data-testid="s9-save-profile-display"
                          className="flex min-h-11 items-center rounded-full bg-ink px-[18px] py-2.5 text-[14px] font-semibold text-paper transition-opacity disabled:opacity-40"
                        >
                          {saving ? "Saving…" : "Save profile"}
                        </button>
                        {savedNote ? (
                          <span className="text-[13px] font-medium text-leaf" data-testid="s9-profile-saved">{savedNote}</span>
                        ) : null}
                        {saveError ? (
                          <span className="text-[13px] font-medium text-[#C41112]" data-testid="s9-profile-error">{saveError}</span>
                        ) : null}
                      </div>

                      {/* Same selection + persist path as the inline five. */}
                      <AvatarLibraryDialog
                        open={libraryOpen}
                        onOpenChange={setLibraryOpen}
                        userId={userId}
                        backgroundColour={bgColour}
                        selectedId={selectedGenId}
                        onSelect={(a: OwnAvatar) => {
                          setSelectedGenId(a.id);
                          setPickedUrl(a.generated_image_url);
                        }}
                      />
                    </>

                  )}
                </div>
              </div>

              {/* Name + default delivery address, editable in place. Profile
                  values win over the latest order; confirmed orders are never
                  retroactively changed (see ProfileDetailsEditor). */}
              <ProfileDetailsEditor
                userId={userId}
                email={null}
                labels={{
                  nameLabel: t("s9.profile.nameLabel"),
                  emailLabel: t("s9.profile.emailLabel"),
                  addressLabel: t("s9.profile.addressLabel"),
                }}
                fallbackName={displayName}
                fallbackAddress={posted}
              />

              {/* Identity change — OTP to the NEW address, security notice to
                  the OLD one. Separate from the profile editor above because
                  this one IS a credential. */}
              <EmailChangeSection email={email} onChanged={(next) => setEmail(next)} />
            </div>
            ) : null}

            {/* Support conversations — account-scoped, no bearer token involved. */}
            {section === "profile" ? (
              <>
                <div className="mb-1.5 mt-[30px]">
                  <SectionTitle>Support conversations</SectionTitle>
                </div>
                <MySupportTickets />
              </>
            ) : null}



            {/* Cards */}
            {section === "cards" ? (
            <>
            <div className="mb-1.5 mt-[30px] flex items-center gap-2.5">
              <SectionTitle>{t("s9.cards.sectionTitle")}</SectionTitle>
              <span className="flex-1" />
              <Link
                to="/create/style"
                onClick={() => navigate(repeatEntryPath(LAUNCH_PROGRAM_SLUG))}
                data-testid="s9-add-another"
                className="flex min-h-11 items-center gap-1.5 py-2.5 text-[15px] font-semibold text-grape hover:text-tangerine"
              >
                {t("s9.cards.addAnother")}
              </Link>
            </div>

            <div className="mb-[30px] flex flex-col gap-2.5">
              {cards.length === 0 ? (
                <div className="rounded-[18px] border border-foreground/10 bg-white p-5 text-[15px] text-foreground/65" data-testid="s9-cards-empty">
                  {t("s9.cards.empty")}{" "}
                  <Link to="/create/style" className="text-azure hover:text-tangerine">
                    {t("s9.cards.startFirst")}
                  </Link>
                  .
                </div>
              ) : (
                cards.map((row) => {
                  const gallery = row.style ? styleMeta[row.style] : undefined;
                  /* B-1 · the STORED colour is bare HSL components; unwrapped it
                     paints nothing, which is why ordered cards looked blank. */
                  const color = toCssColour(row.card_color) || gallery?.color || FALLBACK_TILE_COLOR;
                  const name = gallery?.name || (row.style ? humaniseStyleKey(row.style) : "Card");
                  /* B-1 · framing AS ORDERED: zoom and position come off the row,
                     not from renderer defaults. Preview equals final. */
                  const pos = (row.avatar_position ?? null) as { x?: number; y?: number } | null;
                  const orderedZoom = Number.isFinite(Number(row.avatar_scale))
                    ? Math.min(200, Math.max(50, Number(row.avatar_scale) * 100))
                    : 100;
                  /* N19 · THE card renderer, fed the avatar the customer chose. */
                  const avatarUrl = row.selected_avatar_image_id ? cardArt[row.selected_avatar_image_id] ?? null : null;
                  const status = row.status;
                  const statusLabel = buildStatusCopy(t)[status] ?? status;
                  const tone = STATUS_TONE[status] ?? "bg-foreground/10 text-ink";
                  return (
                    <div
                      key={row.id}
                      data-testid={`s9-card-${row.id}`}
                      className="flex items-center gap-3.5 rounded-[18px] border border-foreground/10 bg-white p-3.5"
                    >
                      <div className="w-[52px] flex-none">
                        <DesignedCardFace
                          card={{
                            avatarUrl,
                            colour: color,
                            orientation: (row.orientation as "portrait" | "landscape") || "portrait",
                            avatarZoom: orderedZoom,
                            avatarPosition: {
                              x: Number.isFinite(Number(pos?.x)) ? Number(pos?.x) : 50,
                              y: Number.isFinite(Number(pos?.y)) ? Number(pos?.y) : 50,
                            },
                          }}
                          fallbackArt={gallery?.preview ?? null}
                          alt={name}
                          className="rounded-lg"
                        />
                      </div>
                      <div className="flex min-w-0 flex-col gap-[3px]">
                        <span className="text-[16px] font-semibold">{name}</span>
                        <span className="text-[13px] text-foreground/70">{cardSubtitle(row, t)}</span>
                      </div>
                      <span className="flex-1" />
                      <span className={`flex-none rounded-full px-[11px] py-1.5 text-[12px] font-semibold ${tone}`}>
                        {statusLabel}
                      </span>
                    </div>
                  );
                })
              )}
            </div>

            <div className="mb-[30px] flex items-start gap-3 rounded-2xl border border-foreground/10 bg-white px-4 py-[15px]">
              <svg viewBox="0 0 20 20" className="mt-px h-[19px] w-[19px] flex-none">
                <circle cx="10" cy="10" r="8.4" fill="none" stroke="#566178" strokeWidth="1.7" />
                <path d="M10 9v5" stroke="#566178" strokeWidth="1.8" strokeLinecap="round" />
                <circle cx="10" cy="6.2" r="1.1" fill="#566178" />
              </svg>
              <span className="text-[14px] leading-[1.55] text-foreground/[0.78]">
                {t("s9.info.bankDisclosure")}
              </span>
            </div>
            </>
            ) : null}

            {/* Sharing & earnings — only when a real referral code exists */}
            {section === "share" && (issuedCode ?? waitlist?.referral_code) ? (
              <>
                {/* Position + movement live at /standing ONLY — this is the
                    reachable link into it, not a second copy of it. Waitlist
                    entrants only: an ordering customer has no place in line. */}
                {waitlist?.referral_code ? (
                <Link
                  to="/standing"
                  data-testid="s9-standing-link"
                  className="mb-4 flex min-h-14 items-center gap-3 rounded-2xl border border-foreground/10 bg-white px-4 py-3 text-[15px] font-semibold text-ink hover:border-ink"
                >
                  See your place in line
                  <span className="flex-1" />
                  <span aria-hidden className="text-grape">→</span>
                </Link>
                ) : null}

                <SectionTitle className="mb-3">{t("s9.sharing.sectionTitle")}</SectionTitle>
                <div className="mb-3 rounded-[20px] bg-ink p-5 text-paper">
                  <span className="text-[12px] font-semibold uppercase tracking-[0.1em] text-paper/60">{t("s9.sharing.yourCode")}</span>
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <span className="font-brand text-[30px] font-semibold tracking-[-0.01em] text-volt" data-testid="s9-referral-code">
                      {issuedCode ?? waitlist?.referral_code}
                    </span>
                    <span className="flex-1" />
                    <button
                      type="button"
                      onClick={copyCode}
                      data-testid="s9-copy-code"
                      className="flex min-h-11 cursor-pointer items-center rounded-full border border-paper/30 bg-transparent px-[18px] py-2.5 text-[14px] font-semibold text-paper transition-colors hover:border-paper"
                    >
                      {copied ? t("s9.sharing.copied") : t("s9.sharing.copyCode")}
                    </button>
                    <Link
                      to="/create/reveal"
                      data-testid="s9-share-card"
                      className="flex min-h-11 items-center rounded-full bg-volt px-[18px] py-2.5 text-[14px] font-semibold text-ink transition-colors hover:bg-paper"
                    >
                      {t("s9.sharing.shareCard")}
                    </Link>
                  </div>
                  {sharingTerms ? (
                    <p className="m-0 mt-3.5 text-[14px] leading-[1.5] text-paper">{sharingTerms}</p>
                  ) : null}
                </div>

                <div className="mb-3 grid grid-cols-2 gap-2.5 sm:grid-cols-2">
                  <div className="flex flex-col gap-1 rounded-2xl border border-foreground/10 bg-white p-[18px]">
                    <span className="font-brand text-[32px] font-semibold leading-none tracking-[-0.02em]" data-testid="s9-referral-count">
                      {referralsMade ?? waitlist?.referral_count ?? 0}
                    </span>
                    <span className="text-[13px] font-medium text-slate-muted">{t("s9.sharing.referralsLabel")}</span>
                  </div>
                  {discountsEarned !== null ? (
                    <div className="flex flex-col gap-1 rounded-2xl bg-volt p-[18px]">
                      <span className="font-brand text-[32px] font-semibold leading-none tracking-[-0.02em]" data-testid="s9-referral-discounts-earned">
                        {discountsEarned}
                      </span>
                      <span className="text-[13px] font-medium text-ink/80">{t("s9.sharing.earnedLabel")}</span>
                    </div>
                  ) : null}
                </div>
              </>

            ) : null}

            {/* B7 · earned referral discounts. Independent of the waitlist
                block: an ordering customer has a referral record whether or not
                they ever joined a waitlist. Counts and terms only, never a
                money balance. */}
            {section === "share" ? <ReferralRecord /> : null}


            <p className="mt-[22px] text-center text-[12.5px] leading-[1.55] text-foreground/70">
              {t("s9.footer.disclaimer")}
            </p>
          </>
        )}
      </FunnelContainer>
    </FunnelPage>
  );
}
